"use client";

import Analytics from "@/app/_components/analytics";
import { Layout } from "@/components";
import { GamePageLayout } from "@/components/NewGamePage/GamePageLayout";
import { ImageBackground } from "@/components/ui/custom/image-background";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { notifyError } from "@/utils/monitoring";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { GameDetailsProvider } from "../../../../context/GameDetailsContext";
import GameDetailsV2 from "../game-details-v2";
import type { GameData } from "../types";

export const SubscribedLayout = ({ gameData }: { gameData: GameData }) => {
  const { newGamePageNov25Gpt } = useFeatureFlags();
  const ldClient = useLDClient();
  const [ldUpdated, setLdUpdated] = useState(false);

  const pathname = usePathname();

  useEffect(() => {
    if (!ldClient || !pathname || ldUpdated) return;

    const updateLdContext = async () => {
      const context = ldClient.getContext();
      try {
        await ldClient.identify({ ...context, pathname: { key: pathname } });
      } catch (error) {
        notifyError(error, "LDContextUpdateError");
      }
      setLdUpdated(true);
    };

    updateLdContext();
  }, [ldClient, pathname, ldUpdated]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: ldUpdated should be reset when pathname changes
  useEffect(() => setLdUpdated(false), [pathname]);

  if (!ldUpdated) return <div className="w-screen h-screen bg-background" />;

  if (newGamePageNov25Gpt && gameData.gameContent?.is_prelaunch) {
    return (
      <Layout disableContainerPadding hideRightNav>
        <GameDetailsProvider gameData={gameData}>
          <GamePageLayout gameData={gameData} />
        </GameDetailsProvider>
      </Layout>
    );
  }

  return (
    <Layout disableContainerPadding>
      {!!gameData.details.background_url && (
        <ImageBackground
          staticImageUrl={gameData.details.background_url}
          className="absolute inset-0"
        />
      )}
      <Analytics screen="Details" userJourney="Games" />
      <GameDetailsV2 gameData={gameData} />
    </Layout>
  );
};
